import { addEvent, removeEvent, triggerEvent } from '../../utils/events';
import { unwrap, wrap, setStyles, hasClass } from '../../utils/html';
import initButtonGroup from '../../components/ButtonGroup';
import formatInlineEdit from './inline-edit/formatInlineEdit';

const detailTableTypeList = {
  detailedView: 'detailedView',
  expandedRow: 'expandedRow',
  stackedRow: 'stackedRow',
};
const overlayActionClasses = {
  open: 'overlay__table--open',
  close: 'overlay__table--close',
  cancel: 'overlay__table--cancel',
  trigger: 'js-table-overlay-trigger',
  applyReorder: 'apply--reorder',
  resetDefault: 'reset-to-default',
  selectAll: 'select-all',
};
export class Table {
  /**
   * @param  {Dom}    table             table reference
   */
  constructor(config) {
    this.table = config.table;

    // Detail View
    this.detailViewType = config.detailViewType;
    this.detailViewSelector = config.detailViewSelector;
    this.detailViewDismissTrigger = document.querySelector(config.detailViewDismissSelector);
    this.inlineAlert = this.table.querySelector('.js-alert-overlay');
    this.overlayTriggers = document.querySelectorAll(`.${ overlayActionClasses.trigger }`);
    this.overlayElement = document.querySelector(config.overlayElement);
    this.overlayCloses = document.querySelectorAll(`.${ overlayActionClasses.close }`);
    this.cancelButton = document.querySelector(`.${ overlayActionClasses.cancel }`);
    this.applyReorder = document.querySelectorAll(`.${ config.trigger }`);
    this.buttonTrigger = document.querySelector(`.${ config.trigger }`);
    this.resetDefault = document.querySelectorAll(`.${ overlayActionClasses.resetDefault }`);
    this.checkboxes = document.querySelectorAll(`.${ overlayActionClasses.selectAll }`);
    this.inlineEdits = this.table.querySelectorAll('.inline--edit');
    this.selector = config.trigger;
    this.tableReorder = config.tableReorder;
    this.buttonGroup = {};

    if (config.overlayElement) {
      this.overlayElementString = config.overlayElement.split('.').join('');
    }

    this.currentTableHeaderOrder = [];
    this.newTableHeaderOrder = [];
    this.newOrderObject = {};
    this.newOrderArray = [];
    this.disabledList = {};
    this.unlockedItems = [];
    this.reorderDefaultState = {};
    this.allRowsSelected = true;

    this.switched = false;
    this.magicBreakpoint = 768;

    if (!this.isEverythingOk()) {
      return;
    }

    this.copy = this.table.cloneNode(true);
    formatInlineEdit(this.copy.querySelectorAll('.inline--edit'));
    this.copyTwo = this.table.cloneNode(true);
    this.copyOverlayElement = {};

    // Initialize table
    this.init();
  }

  init() {
    if (!this.table.classList.contains('table--responsive')) {
      this.table.classList.add('full--scroll');
    }
    if (!this.detailViewType) {
      this.updateTable();
    }
    this.addListeners();
    this.populateReorder();
    this.checkSelectedRows();
    formatInlineEdit(this.inlineEdits);
  }

  destroy() {
    if (!this.detailViewType) {
      this.unsplitTable();
    }
    this.removeListeners();
  }

  /* eslint-disable no-console */
  isEverythingOk() {
    if (!this.table) {
      console.warn('CommonUI - Table - There\'s no table found');
      return false;
    }
    return true;
  }
  /* eslint-enable no-console */

  updateTable() {
    if ((window.innerWidth < this.magicBreakpoint) && !this.switched) {
      this.switched = true;
      this.splitTable();
    } else if (this.switched && (window.innerWidth >= this.magicBreakpoint)) {
      this.switched = false;
      this.unsplitTable();
    }
  }

  splitTable() {
    if (!this.table.classList.contains('full--scroll')) {
      wrap(this.table, 'table--wrapper');
      this.copy.classList.remove('table--responsive');
      this.table.parentElement.appendChild(this.copy);
      wrap(this.copy, 'table--pinned');
      wrap(this.table, 'table--scrollable');
      if (this.inlineAlert) {
        addEvent(this.table.parentElement, 'scroll', this.updateAlertPosition.bind(this));
      }
    }
  }

  unsplitTable() {
    if (!this.table.classList.contains('full--scroll')) {
      unwrap(this.table);
      unwrap(this.table);
      this.copy.parentNode.parentNode.removeChild(this.copy.parentNode);
    }
  }

  addListeners() {
    switch (this.detailViewType) {
      case detailTableTypeList.detailedView:
        for (const detailViewTrigger of this.table.querySelectorAll('.js-table-detail-trigger')) {
          addEvent(detailViewTrigger, 'click', this.showDetailView.bind(this));
        }
        addEvent(this.detailViewDismissTrigger, 'click', this.hideDetailView.bind(this));
        break;
      case detailTableTypeList.expandedRow:
        for (const expandedViewTrigger of this.table.querySelectorAll('.js-table-detail-expand-trigger')) {
          addEvent(expandedViewTrigger, 'click', this.toggleExpandedRow.bind(this));
        }
        break;
      case detailTableTypeList.stackedRow:
        for (const stackedExpandedViewTrigger of this.table.querySelectorAll('.js-table-detail-stacked-trigger')) {
          addEvent(stackedExpandedViewTrigger, 'click', this.toggleStackedExpandedRow.bind(this));
        }
        break;
      default:
        addEvent(window, 'resize', this.updateTable.bind(this));
        break;
    }
    this.overlayListeners();
    addEvent(this.table, 'apply.commonui.inline-edit', () => {
      this.copy = this.table.cloneNode(true);
    });
  }

  updateAlertPosition() {
    if (hasClass(this.inlineAlert, 'is-minimised')) {
      removeEvent(this.table.parentElement, 'scroll', this.updateAlertPosition.bind(this));
      return;
    }
    setStyles(this.inlineAlert, { left: `-${ this.table.parentElement.scrollLeft }px` });
  }

  removeListeners() {
    switch (this.detailViewType) {
      case detailTableTypeList.detailedView:
        for (const detailViewTrigger of this.table.querySelectorAll('.js-table-detail-trigger')) {
          removeEvent(detailViewTrigger, 'click', this.showDetailView.bind(this));
        }
        removeEvent(this.detailViewDismissTrigger, 'click', this.hideDetailView.bind(this));
        break;
      case detailTableTypeList.expandedRow:
        for (const expandedViewTrigger of this.table.querySelectorAll('.js-table-detail-expand-trigger')) {
          removeEvent(expandedViewTrigger, 'click', this.toggleExpandedRow.bind(this));
        }
        break;
      case detailTableTypeList.stackedRow:
        for (const stackedExpandedViewTrigger of this.table.querySelectorAll('.js-table-detail-stacked-trigger')) {
          removeEvent(stackedExpandedViewTrigger, 'click', this.toggleStackedExpandedRow.bind(this));
        }
        break;
      default:
        removeEvent(window, 'resize', this.updateTable.bind(this));
        break;
    }
  }

  overlayListeners() {
    for (const overlayTrigger of this.overlayTriggers) {
      addEvent(overlayTrigger, 'click', this.open.bind(this));
    }
    for (const overlayClose of this.overlayCloses) {
      addEvent(overlayClose, 'click', this.close.bind(this));
    }
    addEvent(this.cancelButton, 'click', this.close.bind(this));
    for (const apply of this.applyReorder) {
      addEvent(apply, 'click', this.reorderTable.bind(this));
    }
    for (const reset of this.resetDefault) {
      addEvent(reset, 'click', this.reset.bind(this));
    }
    for (const checkbox of this.checkboxes) {
      addEvent(checkbox, 'click', this.selectAll.bind(this));
    }
  }

  // Detail View
  showDetailView() {
    if (this.detailViewType === detailTableTypeList.detailedView && this.table) {
      const secondaryView = this.table.querySelector('.js-table-secondary-view');
      secondaryView.style.top = `${ this.table.scrollTop }px`;
      this.table.classList.add('is-active');
    }
  }

  hideDetailView() {
    if (this.detailViewType === detailTableTypeList.detailedView && this.table) {
      this.table.classList.remove('is-active');
    }
  }

  // Expanded Row
  toggleExpandedRow(event) {
    const nextExpandedRow = event.currentTarget.parentNode.nextElementSibling;
    if (nextExpandedRow.classList.contains('is-active')) {
      event.currentTarget.classList.remove('is-active');
      nextExpandedRow.classList.remove('is-active');
    } else {
      event.currentTarget.classList.add('is-active');
      nextExpandedRow.classList.add('is-active');
    }
  }

  // Stacked Expanded Row
  toggleStackedExpandedRow(event) {
    const previousContentSection = event.currentTarget.parentNode.parentNode;
    const expandedContainer = previousContentSection.querySelector('.js-stacked-content');
    if (expandedContainer.classList.contains('is-active')) {
      event.currentTarget.classList.remove('is-active');
      expandedContainer.classList.remove('is-active');
    } else {
      event.currentTarget.classList.add('is-active');
      expandedContainer.classList.add('is-active');
    }
  }

  open(event) {
    if (this.overlayElement &&
      this.overlayElement.classList === event.target.parentElement.children[1].lastElementChild.classList) {
      this.overlayElement.classList.add(overlayActionClasses.open);
    }
  }

  close(event) {
    if (event.target.classList.contains(overlayActionClasses.close) ||
    event.target.classList.contains('apply--reorder')) {
      event.target.parentElement.parentElement.parentElement.classList.remove(overlayActionClasses.open);
    }
    this.reset(event, true);
  }

  populateReorder() {
    if (this.table.classList.contains(this.tableReorder)) {
      const uList = document.createElement('ul');
      uList.classList.add('btn-group', 'js-btn-group');
      for (const row of this.copy.tHead.rows) {
        let index = 0;
        for (const cell of row.cells) {
          this.currentTableHeaderOrder.push(cell.innerText);
          this.newOrderObject[index] = cell.innerText;
          const list = document.createElement('li');
          list.classList.add('btn-group-item');
          list.setAttribute('data-id', index);
          if (cell.classList.contains('table__heading--disable')) {
            this.disabledList[index] = cell.innerText;
            list.setAttribute('disabled', true);
          }

          const input = document.createElement('input');
          input.classList.add('btn-group-item__input');
          input.setAttribute('type', 'checkbox');
          input.setAttribute('id', cell.innerText);
          list.appendChild(input);

          if (input.parentElement.getAttribute('disabled')) {
            input.checked = true;
          }

          if (cell.classList.contains('table--reorder--show-row')) {
            input.checked = true;
          }

          const label = document.createElement('label');
          label.classList.add('btn-group-item__label');
          label.setAttribute('for', cell.innerText);
          label.setAttribute('draggable', true);
          label.innerHTML = cell.innerText;

          const icon = document.createElement('i');
          icon.classList.add('icon-cme-drag-drop');

          label.appendChild(icon);
          list.appendChild(label);
          uList.appendChild(list);

          index += 1;
        }
      }
      this.buttonTrigger.setAttribute('disabled', true);
      this.cancelButton.setAttribute('disabled', true);
      this.overlayElement.lastElementChild.children[1].appendChild(uList);
      this.reorderDefaultState = uList.cloneNode(true);
      this.copyOverlayElement = this.overlayElement.cloneNode(true);
      this.defaultUnlockedItems = this.getUnlockedItems(this.reorderDefaultState);
      this.unlockedItems = this.getUnlockedItems(uList);

      initButtonGroup(uList);

      this.buttonGroup = uList;

      for (const button of this.buttonGroup.children) {
        addEvent(button, 'change', this.checkSelectedRows.bind(this));
      }

      addEvent(uList, 'drop.commonui.buttonGroup', this.enableApply.bind(this));
    }
  }

  getUnlockedItems(list) {
    const unlockedItems = [];
    for (const listItem of list.children) {
      if (!listItem.getAttribute('disabled')) {
        unlockedItems.push(listItem.lastElementChild.innerHTML);
      }
    }

    return unlockedItems;
  }

  createNewTableHeaderOrder(tableHeaderOrder) {
    const newTableHeaderOrder = tableHeaderOrder;
    for (const key in this.disabledList) {
      for (const header in newTableHeaderOrder) {
        if (this.disabledList[key] === newTableHeaderOrder[header]) {
          newTableHeaderOrder.splice(header, 1);
          break;
        }
      }
    }

    for (const key in this.disabledList) {
      newTableHeaderOrder.splice(key, 0, this.disabledList[key]);
    }

    return newTableHeaderOrder;
  }

  reorderTableHeaders() {
    const newOrderArray = [];
    for (const row of this.table.tHead.rows) {
      let index = 0;
      for (const cell of row.cells) {
        cell.innerHTML = this.newTableHeaderOrder[index];
        for (const key in this.newOrderObject) {
          if (cell.innerText === this.newOrderObject[key]) {
            newOrderArray.push(parseInt(key, 10));
          }
        }
        index += 1;
      }
    }

    return newOrderArray;
  }

  reorderTable(event) {
    if (event.target.classList.contains(this.selector) && this.table.classList.contains(this.tableReorder)) {
      for (const tableHeading of this.overlayElement.lastElementChild.children[1].children[1].children) {
        this.newTableHeaderOrder.push(tableHeading.lastElementChild.innerText);
      }

      this.unlockedItems = this.getUnlockedItems(this.overlayElement.lastElementChild.children[1].children[1]);
      this.newTableHeaderOrder = this.createNewTableHeaderOrder(this.newTableHeaderOrder);
      this.newOrderArray = this.reorderTableHeaders();

      let rowIndex = 0;
      for (const row of this.table.rows) {
        let cellIndex = 0;
        if (!row.classList.contains('table--detail__row--expanded') &&
        !row.classList.contains('table--detail__row--stacked')) {
          for (const cell of row.cells) {
            cell.innerHTML = this.copyTwo.rows[rowIndex].cells[this.newOrderArray[cellIndex]].innerHTML;
            cellIndex += 1;
          }
        }
        rowIndex += 1;
      }

      if (this.switched) {
        this.unsplitTable();
      }
      this.copy = this.table.cloneNode(true);
      if (this.switched) {
        this.splitTable();
      }

      this.newTableHeaderOrder = [];
      this.newOrderArray = [];

      triggerEvent(this.table, 'apply.commonui.reorderTable', {
        detail: {
          savedTable: this.table,
        },
      });

      this.close(event);
      this.buttonTrigger.setAttribute('disabled', true);
      this.cancelButton.setAttribute('disabled', true);
    }
  }

  reset(event, savedState) {
    if (event.target.offsetParent.parentElement.parentElement.classList.contains(this.overlayElementString) &&
    this.table.classList.contains(this.tableReorder)) {
      let index = 0;
      for (const child of this.overlayElement.lastElementChild.children[1].lastElementChild.children) {
        if (!child.getAttribute('disabled')) {
          if (savedState) {
            child.lastElementChild.innerHTML = this.unlockedItems[index];
            this.buttonTrigger.setAttribute('disabled', true);
            this.cancelButton.setAttribute('disabled', true);
          } else {
            child.lastElementChild.innerHTML = this.defaultUnlockedItems[index];
            this.enableApply();
          }
          index += 1;
        }
      }
    }
  }

  checkSelectedRows() {
    if (this.table.classList.contains(this.tableReorder)) {
      const selectIcon = this.overlayElement.querySelector('.fa.select-all');
      for (const child of this.buttonGroup.children) {
        if (!child.firstElementChild.checked) {
          this.allRowsSelected = false;
        }
      }

      if (this.allRowsSelected) {
        selectIcon.classList.remove('fa-square-o');
        selectIcon.classList.add('fa-check-square-o');
      } else {
        selectIcon.classList.add('fa-square-o');
        selectIcon.classList.remove('fa-check-square-o');
      }
      this.allRowsSelected = true;
    }
  }

  enableApply() {
    if (this.table.classList.contains(this.tableReorder)) {
      this.buttonTrigger.removeAttribute('disabled');
      this.cancelButton.removeAttribute('disabled');
    }
  }

  selectAll(event) {
    if (event.target.offsetParent.parentElement.parentElement.classList.contains(this.overlayElementString) &&
    this.table.classList.contains(this.tableReorder)) {
      const selectIcon = this.overlayElement.querySelector('.fa.select-all');
      for (const child of this.overlayElement.lastElementChild.children[1].lastElementChild.children) {
        if (!child.firstElementChild.checked) {
          this.allRowsSelected = false;
        }
      }
      for (const child of this.overlayElement.lastElementChild.children[1].lastElementChild.children) {
        if (!child.getAttribute('disabled') && !this.allRowsSelected) {
          child.firstElementChild.checked = true;
          selectIcon.classList.remove('fa-square-o');
          selectIcon.classList.add('fa-check-square-o');
        } else if (!child.getAttribute('disabled') && this.allRowsSelected) {
          child.firstElementChild.checked = false;
          selectIcon.classList.remove('fa-check-square-o');
          selectIcon.classList.add('fa-square-o');
        }
      }
      this.allRowsSelected = !this.allRowsSelected;
    }
  }
}

/**
 * Creates a new responsive table instance
 * @param  {Object} config
 */

export default function initTable(config = {
  selector: '.js-table--responsive',
  detailViewType: null,
  detailViewSelector: '.js-table-secondary-view',
  detailViewDismissSelector: '.js-table-secondary-view--dismiss',
  tableReorder: null,
  overlayElement: null,
  trigger: null,
}) {
  const {
    selector, detailViewType, detailViewSelector, detailViewDismissSelector, tableReorder, overlayElement, trigger,
  } = config;
  let tables = null;
  if (typeof selector === 'string') {
    for (const table of document.querySelectorAll(selector)) {
      tables = new Table({
        table, detailViewType, detailViewSelector, detailViewDismissSelector, tableReorder, overlayElement, trigger,
      });
    }
  }
  return tables;
}



// WEBPACK FOOTER //
// ./js/components/tables/Table.js